import React from 'react';
import { cn } from '../../utils/cn';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'primary' | 'secondary' | 'outline' | 'ghost' | 'link' | 'destructive' | 'gradient' | 'success';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  asChild?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  ariaLabel?: string;
}

const buttonVariants = {
  base: 'relative inline-flex items-center justify-center font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98] transform-gpu overflow-hidden whitespace-nowrap',
  variants: {
    default: 'bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/25 dark:hover:shadow-primary/20',
    primary: 'bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/25 dark:hover:shadow-primary/20',
    destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:shadow-lg hover:shadow-destructive/25',
    outline: 'border-2 border-input bg-background hover:bg-accent hover:text-accent-foreground hover:border-accent-foreground/20',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
    ghost: 'hover:bg-accent hover:text-accent-foreground',
    link: 'text-primary underline-offset-4 hover:underline',
    success: 'bg-success text-success-foreground hover:bg-success/90 hover:shadow-lg hover:shadow-success/25',
    gradient: 'bg-gradient-to-r from-primary to-accent-purple text-white hover:shadow-xl hover:shadow-primary/30 hover:scale-[1.02] bg-[length:200%_100%] hover:bg-[position:100%_0%] transition-all duration-500',
  },
  sizes: {
    default: 'h-11 min-h-[44px] px-6 py-2 text-base rounded-xl',
    sm: 'h-9 min-h-[36px] px-4 text-sm rounded-lg',
    lg: 'h-14 min-h-[56px] px-8 text-lg rounded-2xl',
    icon: 'h-11 w-11 min-h-[44px] min-w-[44px] rounded-xl',
  },
};

export function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  loading = false,
  icon,
  iconPosition = 'left',
  disabled,
  children,
  ariaLabel,
  ...props
}: ButtonProps) {
  const Comp = asChild ? 'span' : 'button';
  
  return (
    <Comp
      className={cn(
        buttonVariants.base,
        buttonVariants.variants[variant],
        buttonVariants.sizes[size],
        loading && 'cursor-wait',
        // Touch-specific adjustments
        'touch:min-h-[48px] touch:px-7',
        className
      )}
      disabled={disabled || loading}
      aria-label={ariaLabel || (loading ? 'Loading...' : undefined)}
      aria-busy={loading}
      aria-disabled={disabled || loading}
      {...props}
    >
      {/* Ripple effect container */}
      <span className="absolute inset-0 overflow-hidden rounded-[inherit]">
        <span className="absolute inset-0 bg-white opacity-0 hover:opacity-10 transition-opacity duration-300" />
      </span>
      
      {/* Content */}
      <span className="relative z-10 flex items-center gap-2">
        {loading ? (
          <>
            <svg
              className="animate-spin h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            {children}
          </>
        ) : (
          <>
            {icon && iconPosition === 'left' && icon}
            {children}
            {icon && iconPosition === 'right' && icon}
          </>
        )}
      </span>
    </Comp>
  );
}

export default Button;
